import React from "react";
import DOMPurify from "dompurify";

export const sanitizeHtml = (rawHtml) => {
  if (!rawHtml) return "";

  return DOMPurify.sanitize(String(rawHtml), {
    USE_PROFILES: { html: true },
    FORBID_TAGS: [
      "style",
      "iframe",
      "object",
      "embed",
      "form",
      "input",
      "button",
      "textarea",
      "select",
    ],
    FORBID_ATTR: ["style"],
    ALLOW_UNKNOWN_PROTOCOLS: false,
    ADD_ATTR: ["target", "rel"],
  });
};

const ATTRIBUTE_NAME_MAP = {
  class: "className",
  tabindex: "tabIndex",
  colspan: "colSpan",
  rowspan: "rowSpan",
};

const getElementProps = (node, key) => {
  const props = { key };

  Array.from(node.attributes).forEach(({ name, value }) => {
    if (!value || name.startsWith("on") || name === "style") {
      return;
    }

    const propName = ATTRIBUTE_NAME_MAP[name] || name;
    props[propName] = value;
  });

  if (node.tagName.toLowerCase() === "a" && props.target === "_blank") {
    props.rel = "noopener noreferrer";
  }

  return props;
};

const createReactNode = (node, key) => {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent;
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const tagName = node.tagName.toLowerCase();
  const props = getElementProps(node, key);
  const children = Array.from(node.childNodes)
    .map((child, index) => createReactNode(child, `${key}-${index}`))
    .filter((child) => child !== null && child !== undefined && child !== "");

  return React.createElement(tagName, props, children.length > 0 ? children : undefined);
};

export const renderSanitizedHtml = (rawHtml) => {
  const sanitizedHtml = sanitizeHtml(rawHtml);

  if (!sanitizedHtml) {
    return null;
  }

  if (typeof DOMParser === "undefined") {
    return sanitizedHtml.replace(/<[^>]*>/g, "");
  }

  const parser = new DOMParser();
  const document = parser.parseFromString(`<body>${sanitizedHtml}</body>`, "text/html");

  return Array.from(document.body.childNodes)
    .map((node, index) => createReactNode(node, `sanitized-${index}`))
    .filter((node) => node !== null && node !== undefined && node !== "");
};

export const SanitizedHtml = ({ as: Component = "div", html, ...props }) => {
  const content = renderSanitizedHtml(html);

  return React.createElement(Component, props, content);
};
